import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type FieldError, useFormContext } from 'react-hook-form';
import config from '../../config';
import ErrorMessage from './ErrorMessage';
import { validateField } from '../../utils/form-utils';
import type {
  AthenaPageLoadSelectOptions,
  ErrorMessages,
  Question,
  Rules,
  SelectOption,
  SelectOptionDefault,
  SelectOptionGroup,
  SelectOptionsSource,
} from '../../config/types';
import { AuthContext } from '../../auth/AuthContext';
import { queryAthenaLookup } from '../../service/form';
import type { AthenaLookupEnvironment, FormAssets } from '../../service/types';
import Details from './Details';

import {
  getConditionalQuestionDefaultValues,
  getConditionalQuestionNames,
  getSelectedConditionalQuestions,
} from '../../utils/config-utils';
import {
  getSingleSelectableOptionValue,
  getSelectOptionLabel,
  getSelectOptionValue,
  resolveSelectOptions,
} from '../../utils/select-assets';
import TextInputField from './TextInputField';
import Textarea from './Textarea';
import Fieldset from './Fieldset';
import ScsObjectivesShareAction from '../ScsObjectivesShareAction/ScsObjectivesShareAction';

type Props = {
  name: string;
  options: SelectOptionsSource;
  hintOption?: string;
  numeric?: boolean;
  error?: FieldError;
  disabled?: boolean;
  rules?: Rules;
  title: string;
  errorMessages?: ErrorMessages;
  detailsHeading?: string;
  detailsContent?: string;
  defaultValue?: string;
  assets?: FormAssets;
  autoFillSingleOptionAndHide?: boolean;
  showRequiredFlag?: boolean;
  scsObjectivesShare?: {
    answers: Record<string, unknown>;
    formId?: string;
    formVersion?: number;
  };
} & ComponentPropsWithoutRef<'select'>;

const DEFAULT_ATHENA_PAGE_LOAD_MAX_ROWS = 50;
type AthenaLookupRow = Record<string, string | null | undefined>;

const isAthenaPageLoadSelectOptions = (
  value: SelectOptionsSource | undefined,
): value is AthenaPageLoadSelectOptions =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  'source' in value &&
  value.source === 'athenaPageLoad';

const resolveAthenaEnvironment = (
  configuredEnvironment: AthenaPageLoadSelectOptions['environment'],
): AthenaLookupEnvironment => {
  if (configuredEnvironment === 'prod' || configuredEnvironment === 'preprod') {
    return configuredEnvironment;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const apiUrl = String(config.apiUrl || '').toLowerCase();
  return hostname.includes('preprod') || apiUrl.includes('preprod') ? 'preprod' : 'prod';
};

export default function SelectInputField(props: Props) {
  const {
    name,
    rules,
    options,
    hintOption,
    disabled,
    defaultValue,
    title,
    id,
    errorMessages,
    detailsHeading,
    detailsContent,
    assets,
    autoFillSingleOptionAndHide,
    showRequiredFlag,
    scsObjectivesShare,
  } = props;
  const ariaLabelledBy = props['aria-labelledby'];

  const { register, watch, getFieldState, getValues, setValue, trigger, unregister } =
    useFormContext();
  const authContext = useContext(AuthContext);
  const [athenaPageLoadRows, setAthenaPageLoadRows] = useState<AthenaLookupRow[] | null>(null);
  const [athenaPageLoadError, setAthenaPageLoadError] = useState<string | null>(null);
  const [isAthenaPageLoadPending, setIsAthenaPageLoadPending] = useState(false);
  const [scsObjectivesEmailedAddresses, setScsObjectivesEmailedAddresses] = useState<string[]>([]);
  const latestAthenaRequestId = useRef(0);
  const athenaOptionsSource = useMemo(
    () => (isAthenaPageLoadSelectOptions(options) ? options : null),
    [options],
  );

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, rules || {}, value, errorMessages),
  });

  const { error } = getFieldState(name);
  const selected = watch(name) ?? defaultValue;
  const allValues = watch();

  const fetchJWT = authContext?.fetchJWT;

  useEffect(() => {
    if (!athenaOptionsSource) {
      setAthenaPageLoadRows(null);
      setAthenaPageLoadError(null);
      setIsAthenaPageLoadPending(false);
      return;
    }

    if (!fetchJWT) {
      setAthenaPageLoadRows(null);
      setAthenaPageLoadError('Unable to load options right now. Please refresh and try again.');
      setIsAthenaPageLoadPending(false);
      return;
    }

    const requestId = latestAthenaRequestId.current + 1;
    latestAthenaRequestId.current = requestId;
    setAthenaPageLoadError(null);
    setIsAthenaPageLoadPending(true);

    const loadOptions = async () => {
      try {
        const { idToken } = await fetchJWT({ redirectOnFail: false });
        if (!idToken) {
          throw new Error('Missing auth token');
        }

        const response = await queryAthenaLookup(idToken, {
          lookupType: athenaOptionsSource.lookupType,
          environment: resolveAthenaEnvironment(athenaOptionsSource.environment),
          maxRows: athenaOptionsSource.maxRows ?? DEFAULT_ATHENA_PAGE_LOAD_MAX_ROWS,
          searchTerm: athenaOptionsSource.searchTerm,
        });

        if (latestAthenaRequestId.current === requestId) {
          setAthenaPageLoadRows(response.rows || []);
        }
      } catch (_error) {
        if (latestAthenaRequestId.current === requestId) {
          setAthenaPageLoadRows(null);
          setAthenaPageLoadError('Unable to load options right now. Please refresh and try again.');
        }
      } finally {
        if (latestAthenaRequestId.current === requestId) {
          setIsAthenaPageLoadPending(false);
        }
      }
    };

    void loadOptions();
  }, [athenaOptionsSource, fetchJWT]);

  const filteredAthenaRows = useMemo(() => {
    if (!athenaOptionsSource || !athenaPageLoadRows) {
      return [];
    }

    const filterBy = athenaOptionsSource.filterBy;
    if (!filterBy) {
      return athenaPageLoadRows;
    }

    const answerValue = allValues?.[filterBy.answerField];
    const normalizedAnswer = typeof answerValue === 'string' ? answerValue.trim() : '';
    if (!normalizedAnswer) {
      return [];
    }

    return athenaPageLoadRows.filter((row) => {
      const lookupValue = row?.[filterBy.lookupField];
      return typeof lookupValue === 'string' && lookupValue.trim() === normalizedAnswer;
    });
  }, [allValues, athenaOptionsSource, athenaPageLoadRows]);

  const resolvedOptions = useMemo(() => {
    if (athenaOptionsSource) {
      const values = filteredAthenaRows
        .map((row) => row?.[athenaOptionsSource.valueField])
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map((value) => value.trim());

      return Array.from(new Set(values)).sort((left, right) =>
        left.localeCompare(right, undefined, { sensitivity: 'base' }),
      );
    }

    return resolveSelectOptions({
      options,
      questionName: name,
      assets,
      answers: allValues,
    });
  }, [allValues, assets, athenaOptionsSource, filteredAthenaRows, name, options]);

  const setDerivedAthenaFields = (selectedValue: string) => {
    if (!athenaOptionsSource?.derivedFields || !athenaPageLoadRows) {
      return;
    }

    const selectedRow = filteredAthenaRows.find((row) => {
      const value = row?.[athenaOptionsSource.valueField];
      return typeof value === 'string' && value.trim() === selectedValue;
    });

    Object.entries(athenaOptionsSource.derivedFields).forEach(([fieldName, lookupField]) => {
      const derivedValue = selectedRow?.[lookupField];
      const normalizedDerivedValue = typeof derivedValue === 'string' ? derivedValue.trim() : '';
      if (getValues(fieldName) !== normalizedDerivedValue) {
        setValue(fieldName, normalizedDerivedValue, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    });
  };

  useEffect(() => {
    if (!athenaOptionsSource?.derivedFields || !athenaPageLoadRows) {
      return;
    }

    const selectedValue = typeof selected === 'string' ? selected.trim() : '';
    const selectedRow = filteredAthenaRows.find((row) => {
      const value = row?.[athenaOptionsSource.valueField];
      return typeof value === 'string' && value.trim() === selectedValue;
    });

    if (athenaOptionsSource.filterBy && selectedValue && !selectedRow) {
      if (getValues(name) !== '') {
        setValue(name, '', { shouldDirty: true, shouldValidate: true });
      }
      setDerivedAthenaFields('');
      return;
    }

    setDerivedAthenaFields(selectedValue);
  }, [
    athenaOptionsSource,
    athenaPageLoadRows,
    filteredAthenaRows,
    getValues,
    name,
    selected,
    setValue,
  ]);

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    void hooksFormRegister.onChange(event);
    const selectedValue = event.target.value;
    setValue(name, selectedValue, { shouldDirty: true, shouldValidate: true });
    setDerivedAthenaFields(selectedValue);
  };

  const singleSelectableOptionValue = useMemo(
    () => getSingleSelectableOptionValue(resolvedOptions),
    [resolvedOptions],
  );

  const hideSelect =
    autoFillSingleOptionAndHide === true && singleSelectableOptionValue !== undefined;

  // Initialise the field when it is first shown, or auto-fill it when the only valid
  // choice is already known from the conditional options.
  useEffect(() => {
    if (hideSelect && getValues(name) !== singleSelectableOptionValue) {
      setValue(name, singleSelectableOptionValue, { shouldValidate: true });
      return;
    }

    if (!hideSelect && getValues(name) === undefined) {
      setValue(name, defaultValue ?? '');
    }
  }, [defaultValue, getValues, hideSelect, name, setValue, singleSelectableOptionValue]);

  const optionsForConditional = Array.isArray(options) ? options : resolvedOptions;

  // Correct behaviour: pass full question props
  const conditionalQuestions = getSelectedConditionalQuestions(
    { ...(props as any), options: optionsForConditional },
    selected,
  );
  const conditionalQuestionNames = useMemo(
    () => getConditionalQuestionNames(conditionalQuestions),
    [conditionalQuestions],
  );
  const previousConditionalQuestions = useRef<Question[]>([]);

  useEffect(() => {
    conditionalQuestions.forEach((question) => {
      if (
        question.readOnly &&
        typeof question.defaultValue === 'string' &&
        getValues(question.name) !== question.defaultValue
      ) {
        setValue(question.name, question.defaultValue, { shouldDirty: true, shouldValidate: true });
      }
    });
    const visibleQuestionNames = new Set(conditionalQuestionNames);
    const hiddenQuestions = previousConditionalQuestions.current.filter(
      (question) => typeof question.name === 'string' && !visibleQuestionNames.has(question.name),
    );

    if (hiddenQuestions.length > 0) {
      const resetValues = getConditionalQuestionDefaultValues(hiddenQuestions);
      hiddenQuestions.forEach((question) => {
        if (question.readOnly) resetValues[question.name] = '';
      });
      Object.entries(resetValues).forEach(([questionName, resetValue]) => {
        setValue(questionName, resetValue, { shouldDirty: true, shouldValidate: false });
      });
      unregister(getConditionalQuestionNames(hiddenQuestions), {
        keepDefaultValue: true,
        keepValue: true,
      });
    }

    previousConditionalQuestions.current = conditionalQuestions;
  }, [conditionalQuestionNames, conditionalQuestions, getValues, setValue, unregister]);

  return (
    <>
      {!hideSelect && error && <ErrorMessage error={error} />}
      {!hideSelect && detailsHeading && (
        <Details heading={detailsHeading} content={detailsContent} />
      )}

      {hideSelect ? (
        <input id={id} type="hidden" {...hooksFormRegister} value={singleSelectableOptionValue} />
      ) : (
        <select
          {...hooksFormRegister}
          onChange={handleSelectChange}
          value={athenaOptionsSource ? (selected ?? '') : undefined}
          id={id}
          aria-labelledby={ariaLabelledBy}
          className={`govuk-select ${error && 'govuk-select--error'}`}
          disabled={(disabled ?? false) || isAthenaPageLoadPending}
        >
          {hintOption && <option value="">{hintOption}</option>}

          {resolvedOptions.map((option, index) => {
            if (typeof option === 'string') {
              return (
                <option value={option} key={`select-${option}-${index}`}>
                  {option}
                </option>
              );
            }

            if ('label' in option && 'options' in option) {
              const group = option as SelectOptionGroup;
              return (
                <optgroup label={group.label} key={`optgroup-${group.label}-${index}`}>
                  {group.options.map((opt) => (
                    <option value={opt} key={`select-${opt}`}>
                      {opt}
                    </option>
                  ))}
                </optgroup>
              );
            }

            const def = option as SelectOptionDefault;
            const optionValue = getSelectOptionValue(def);
            if (def.retired && selected !== optionValue) return null;
            return (
              <option
                value={optionValue}
                key={`select-${optionValue}-${index}`}
                disabled={def.retired}
              >
                {getSelectOptionLabel(def)}
              </option>
            );
          })}
        </select>
      )}
      {athenaOptionsSource && isAthenaPageLoadPending && (
        <p className="govuk-hint govuk-!-margin-top-2">Loading options... (please wait)</p>
      )}
      {athenaOptionsSource && athenaPageLoadError && (
        <p className="govuk-error-message govuk-!-margin-top-2">{athenaPageLoadError}</p>
      )}

      {/* Now identical to radio: each child wrapped in Fieldset */}
      {conditionalQuestions.map((cq) => {
        const childName = cq.name;
        const defaultChildValue = typeof cq.defaultValue === 'string' ? cq.defaultValue : '';
        const childError = getFieldState(childName).error;
        const hideSingleOptionChildSelect =
          cq.questionType === 'select' &&
          getSingleSelectableOptionValue(
            resolveSelectOptions({
              options: cq.options,
              questionName: childName,
              assets,
              answers: allValues,
            }),
          ) !== undefined;

        if (cq.questionType === 'select' && hideSingleOptionChildSelect) {
          return (
            <SelectInputField
              key={childName}
              name={childName}
              id={`${childName}-select`}
              hintOption={cq.hintOption}
              options={cq.options || []}
              assets={assets}
              disabled={disabled}
              rules={cq.rules}
              title={cq.title}
              errorMessages={cq.errorMessages}
              detailsHeading={cq.detailsHeading}
              detailsContent={cq.detailsContent}
              defaultValue={typeof cq.defaultValue === 'string' ? cq.defaultValue : undefined}
              autoFillSingleOptionAndHide
              showRequiredFlag={showRequiredFlag}
            />
          );
        }

        if (cq.questionType === 'hidden') {
          const hiddenRegistration = register(childName, {
            validate: (value: string) =>
              validateField(cq.title, cq.rules || {}, value, cq.errorMessages),
          });

          return (
            <div
              key={childName}
              className={`govuk-form-group ${childError ? 'govuk-form-group--error' : ''}`}
              data-testid={`question-action-${childName}`}
            >
              {childError && <ErrorMessage error={childError} />}
              {cq.actionButtonText &&
                (cq.actionType === 'scsObjectivesShare' && scsObjectivesShare ? (
                  <ScsObjectivesShareAction
                    answers={scsObjectivesShare.answers}
                    buttonText={cq.actionButtonText}
                    disabled={disabled}
                    emailFieldName="team-member-email-addresses"
                    formId={scsObjectivesShare.formId}
                    formVersion={scsObjectivesShare.formVersion}
                    onSentEmailsChange={setScsObjectivesEmailedAddresses}
                    statusFieldName={childName}
                  />
                ) : (
                  <button
                    className="govuk-button"
                    type="button"
                    data-module="govuk-button"
                    onClick={() => void trigger(conditionalQuestionNames)}
                    disabled={disabled}
                  >
                    {cq.actionButtonText}
                  </button>
                ))}
              <input type="hidden" {...hiddenRegistration} />
            </div>
          );
        }

        return (
          <div
            key={childName}
            className="govuk-radios__conditional govuk-radios__conditional--visible govuk-!-margin-top-4"
          >
            <Fieldset
              title={cq.title}
              hintText={cq.hint}
              legendId={`${childName}-legend`}
              showRequiredAsterisk={showRequiredFlag === true && cq.rules?.required === true}
              data-testid={`question-fieldset-${childName}`}
            >
              <>
                {cq.questionType === 'textInput' && (
                  <TextInputField
                    title={cq.title}
                    name={childName}
                    readOnly={cq.readOnly}
                    approver={cq.approver}
                    allowSelfApprover={cq.allowSelfApprover}
                    restrictDomains={cq.restrictDomains}
                    placeholder={cq.placeholder}
                    wordCount={cq.wordCount}
                    characterCount={cq.characterCount}
                    rules={cq.rules}
                    defaultValue={defaultChildValue}
                    errorMessages={cq.errorMessages}
                    detailsHeading={cq.detailsHeading}
                    detailsContent={cq.detailsContent}
                    disabled={disabled}
                  />
                )}

                {cq.questionType === 'textarea' && (
                  <Textarea
                    title={cq.title}
                    name={childName}
                    placeholder={cq.placeholder}
                    wordCount={cq.wordCount}
                    characterCount={cq.characterCount}
                    textRows={cq.textRows}
                    emailList={cq.emailList}
                    emailedAddresses={scsObjectivesEmailedAddresses}
                    rules={cq.rules}
                    defaultValue={defaultChildValue}
                    errorMessages={cq.errorMessages}
                    detailsHeading={cq.detailsHeading}
                    detailsContent={cq.detailsContent}
                    disabled={disabled}
                  />
                )}

                {cq.questionType === 'select' && (
                  <SelectInputField
                    name={childName}
                    id={`${childName}-select`}
                    aria-labelledby={`${childName}-legend`}
                    hintOption={cq.hintOption}
                    options={cq.options || []}
                    assets={assets}
                    disabled={disabled}
                    rules={cq.rules}
                    title={cq.title}
                    errorMessages={cq.errorMessages}
                    detailsHeading={cq.detailsHeading}
                    detailsContent={cq.detailsContent}
                    defaultValue={typeof cq.defaultValue === 'string' ? cq.defaultValue : undefined}
                    showRequiredFlag={showRequiredFlag}
                  />
                )}
              </>
            </Fieldset>
          </div>
        );
      })}
    </>
  );
}
